import React, { useEffect, useRef } from 'react';

/**
 * StarfieldBG — super-light canvas starfield for the admin sci-fi backdrop.
 * No deps, pointer-events-none, DPR-aware, pauses when tab hidden.
 */
export const StarfieldBG = ({ density = 90, speed = 0.015 }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let raf;
    let stars = [];
    let dpr = Math.min(window.devicePixelRatio || 1, 2);

    const resize = () => {
      const parent = canvas.parentElement;
      if (!parent) return;
      const { clientWidth: w, clientHeight: h } = parent;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      // regenerate stars
      stars = Array.from({ length: density }, () => ({
        x: Math.random() * w,
        y: Math.random() * h,
        z: Math.random() * 0.9 + 0.1,
        hue: Math.random() > 0.72 ? '#39FF14' : (Math.random() > 0.5 ? '#FF6B1A' : '#FFFFFF'),
        twinkle: Math.random() * Math.PI * 2,
      }));
    };
    resize();
    const ro = new ResizeObserver(resize);
    if (canvas.parentElement) ro.observe(canvas.parentElement);

    let paused = false;
    const onVis = () => { paused = document.hidden; };
    document.addEventListener('visibilitychange', onVis);

    const tick = () => {
      raf = requestAnimationFrame(tick);
      if (paused) return;
      const w = canvas.width / dpr;
      const h = canvas.height / dpr;
      ctx.clearRect(0, 0, w, h);
      for (const s of stars) {
        s.y += speed * (0.3 + s.z * 1.8);
        if (s.y > h) s.y = 0;
        s.twinkle += 0.03;
        const alpha = 0.3 + (Math.sin(s.twinkle) * 0.5 + 0.5) * 0.6 * s.z;
        ctx.beginPath();
        ctx.fillStyle = s.hue;
        ctx.globalAlpha = alpha;
        ctx.arc(s.x, s.y, 0.4 + s.z * 1.1, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.globalAlpha = 1;
    };
    tick();

    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener('visibilitychange', onVis);
      ro.disconnect();
    };
  }, [density, speed]);

  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden">
      <canvas ref={canvasRef} />
      {/* Grid lines overlay */}
      <div
        className="absolute inset-0 opacity-[0.09]"
        style={{
          backgroundImage:
            'linear-gradient(to right, rgba(57,255,20,0.5) 1px, transparent 1px), linear-gradient(to bottom, rgba(57,255,20,0.5) 1px, transparent 1px)',
          backgroundSize: '80px 80px',
          maskImage: 'radial-gradient(ellipse at 50% 20%, rgba(0,0,0,1) 20%, rgba(0,0,0,0) 70%)',
          WebkitMaskImage: 'radial-gradient(ellipse at 50% 20%, rgba(0,0,0,1) 20%, rgba(0,0,0,0) 70%)',
        }}
      />
      {/* Scan line */}
      <div
        className="absolute left-0 right-0 h-24 opacity-[0.07]"
        style={{
          background: 'linear-gradient(180deg, transparent 0%, #39FF14 50%, transparent 100%)',
          animation: 'admin-scan 7s linear infinite',
        }}
      />
      <style>{`@keyframes admin-scan { 0% { top: -10%; } 100% { top: 110%; } }`}</style>
    </div>
  );
};

export default StarfieldBG;
